// src/components/Filters.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants/constants';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
} from '@mui/material';

const Filters = ({ userLevel, privileges, setFilters, hasAccess }) => {
  const [districts, setDistricts] = useState([]);
  const [taluks, setTaluks] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedTaluk, setSelectedTaluk] = useState('');
  const [selectedPincode, setSelectedPincode] = useState('');

  useEffect(() => {
    if (userLevel === 1 || userLevel === 2) {
      fetchDistricts();
    } else if (userLevel === 3 || (userLevel > 3 && hasAccess())) {
      setPincodes(privileges.pincodes || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userLevel, privileges]);

  const fetchDistricts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/districts`);
      if (response.status === 200) {
        setDistricts(response.data.districts || response.data);
      } else {
        console.error('Failed to fetch districts');
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
    }
  };

  const fetchTaluks = async (districtId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/taluks/district/${districtId}`);
      if (response.status === 200) {
        setTaluks(response.data.taluks || response.data);
      } else {
        setTaluks([]);
        console.error('Failed to fetch taluks');
      }
    } catch (error) {
      setTaluks([]);
      if (error.response && error.response.status === 404) {
        console.error('No taluks found for the given districtId');
      } else {
        console.error('Error fetching Constituency:', error);
      }
    }
  };

  const fetchPincodes = async (talukId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/pincodes/taluk/${talukId}`);
      if (response.status === 200) {
        setPincodes(response.data.pincodes);
      } else {
        setPincodes([]);
        console.error('Failed to fetch pincodes');
      }
    } catch (error) {
      setPincodes([]);
      if (error.response && error.response.status === 404) {
        console.error('No pincodes found for the given talukId');
      } else {
        console.error('Error fetching pincodes:', error);
      }
    }
  };

  const handleDistrictChange = (event) => {
    const districtId = event.target.value;
    setSelectedDistrict(districtId);
    setSelectedTaluk('');
    setSelectedPincode('');
    setTaluks([]);
    setPincodes([]);

    // Fetch taluks for the selected district
    fetchTaluks(districtId);

    // Update filters, resetting talukId and pincode
    setFilters({
      districtId,
      talukId: null,
      pincode: null,
    });
  };

  const handleTalukChange = (event) => {
    const talukId = event.target.value;
    setSelectedTaluk(talukId);
    setSelectedPincode('');
    setPincodes([]);

    // Fetch pincodes for the selected taluk
    fetchPincodes(talukId);

    // Update filters, resetting pincode
    setFilters((prevFilters) => ({
      ...prevFilters,
      talukId,
      pincode: null,
    }));
  };

  const handlePincodeChange = (event) => {
    const pincode = event.target.value;
    setSelectedPincode(pincode);

    // Update filters
    setFilters((prevFilters) => ({
      ...prevFilters,
      pincode,
    }));
  };

  return (
    <Grid container spacing={2} alignItems="center">
      {(userLevel === 1 || userLevel === 2) && (
        <React.Fragment>
          {/* District Filter */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="district-label">District</InputLabel>
              <Select
                labelId="district-label"
                value={selectedDistrict}
                label="District"
                onChange={handleDistrictChange}
              >
                {districts.map((district) => (
                  <MenuItem key={district._id} value={district._id}>
                    {district.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Constituency Filter */}
          {selectedDistrict && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={taluks.length === 0}>
                <InputLabel id="taluk-label">CONSTITUENCY</InputLabel>
                <Select
                  labelId="taluk-label"
                  value={selectedTaluk}
                  label="CONSTITUENCY"
                  onChange={handleTalukChange}
                >
                  {taluks.length > 0 ? (
                    taluks.map((taluk) => (
                      <MenuItem key={taluk._id} value={taluk._id}>
                        {taluk.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">
                      <em>No constituency available</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Pincode Filter */}
          {selectedTaluk && (
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth disabled={pincodes.length === 0}>
                <InputLabel id="pincode-label">Pincode</InputLabel>
                <Select
                  labelId="pincode-label"
                  value={selectedPincode}
                  label="Pincode"
                  onChange={handlePincodeChange}
                >
                  {pincodes.length > 0 ? (
                    pincodes.map((pincode) => (
                      <MenuItem key={pincode} value={pincode}>
                        {pincode}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="">
                      <em>No pincodes available</em>
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}
        </React.Fragment>
      )}
      {(userLevel === 3 || (userLevel > 3 && hasAccess())) && (
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel id="pincode-label">Pincode</InputLabel>
            <Select
              labelId="pincode-label"
              value={selectedPincode}
              label="Pincode"
              onChange={handlePincodeChange}
            >
              {privileges.pincodes && privileges.pincodes.length > 0 ? (
                privileges.pincodes.map((pincode) => (
                  <MenuItem key={pincode} value={pincode}>
                    {pincode}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="">
                  <em>No pincodes available</em>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        </Grid>
      )}
    </Grid>
  );
};

export default Filters;
